import {
  Box,
  Checkbox,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";

import type { EnrichedLine } from "../../services/distributionPreviewService";

const SOURCE_COLOR: Record<
  EnrichedLine["source_type"],
  "default" | "info" | "warning"
> = {
  seeding: "default",
  stock_alert: "warning",
  reorder: "info",
  reposicion: "info",
};

/**
 * Tabla anidada de líneas activas (a transferir) que se muestra al
 * expandir un bucket de proveedor en `ActiveLinesBySupplierPanel`.
 * Auditoría 2026-05-05.
 */
export interface ActiveLinesTableProps {
  lines: EnrichedLine[];
  excludedSet: Set<string>;
  onToggle: (recommendation_id: string) => void;
}

export function ActiveLinesTable({
  lines,
  excludedSet,
  onToggle,
}: ActiveLinesTableProps) {
  return (
    <TableContainer
      sx={{
        bgcolor: "grey.50",
        borderTop: 1,
        borderColor: "divider",
      }}
    >
      <Table size="small">
        <TableHead>
          <TableRow sx={{ bgcolor: "grey.100" }}>
            <TableCell padding="checkbox" sx={{ width: 40 }} />
            <TableCell sx={{ fontWeight: 600, fontSize: "0.75rem" }}>
              Producto
            </TableCell>
            <TableCell
              sx={{ fontWeight: 600, fontSize: "0.75rem", width: 80 }}
            >
              Origen
            </TableCell>
            <TableCell
              align="right"
              sx={{ fontWeight: 600, fontSize: "0.75rem", width: 90 }}
            >
              Stock
            </TableCell>
            <TableCell
              align="right"
              sx={{ fontWeight: 600, fontSize: "0.75rem", width: 90 }}
            >
              Qty
            </TableCell>
            <TableCell
              sx={{ fontWeight: 600, fontSize: "0.75rem", width: 110 }}
            >
              Source
            </TableCell>
            <TableCell
              sx={{ fontWeight: 600, fontSize: "0.75rem", width: 130 }}
            >
              Denegación reclamada
            </TableCell>
            <TableCell
              align="center"
              sx={{ fontWeight: 600, fontSize: "0.75rem", width: 60 }}
            >
              ABC
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {lines.map((line) => {
            const excluded = excludedSet.has(line.recommendation_id);
            return (
              <TableRow
                key={line.recommendation_id}
                hover
                sx={{
                  opacity: excluded ? 0.4 : 1,
                  "& > td": { fontSize: "0.75rem" },
                }}
              >
                <TableCell padding="checkbox">
                  <Checkbox
                    size="small"
                    checked={!excluded}
                    onChange={() => onToggle(line.recommendation_id)}
                    inputProps={{
                      "aria-label": `Incluir línea ${line.unified_product_id}`,
                    }}
                  />
                </TableCell>
                <TableCell>
                  <Tooltip
                    title={line.product_name ?? line.unified_product_id}
                    placement="top"
                  >
                    <Box
                      sx={{
                        maxWidth: 420,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      <Typography variant="caption" fontWeight={500}>
                        {line.product_name ?? "—"}
                      </Typography>
                      <Typography
                        variant="caption"
                        component="div"
                        color="text.secondary"
                        sx={{
                          fontFamily: "monospace",
                          fontSize: "0.65rem",
                        }}
                      >
                        {line.unified_product_id}
                      </Typography>
                    </Box>
                  </Tooltip>
                </TableCell>
                <TableCell>
                  <Typography
                    component="span"
                    variant="caption"
                    sx={{ fontFamily: "monospace", color: "text.secondary" }}
                  >
                    {line.origin_warehouse}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Tooltip
                    title={`Stock disponible en ${line.origin_warehouse}`}
                  >
                    <Chip
                      size="small"
                      color={line.stock_suficiente ? "success" : "error"}
                      label={line.stock_en_origen.toLocaleString("es-MX")}
                      sx={{
                        height: 20,
                        fontSize: "0.65rem",
                        fontWeight: 600,
                      }}
                    />
                  </Tooltip>
                </TableCell>
                <TableCell
                  align="right"
                  sx={{
                    fontVariantNumeric: "tabular-nums",
                    fontWeight: 500,
                  }}
                >
                  {line.qty_planned.toLocaleString("es-MX")}
                  {line.pack_size > 1 && (
                    <Typography
                      component="span"
                      variant="caption"
                      color="text.secondary"
                      sx={{ ml: 0.5 }}
                    >
                      (×{line.pack_size})
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    color={SOURCE_COLOR[line.source_type]}
                    label={line.source_type}
                    sx={{ height: 20, fontSize: "0.65rem" }}
                  />
                </TableCell>
                <TableCell>
                  {line.denial ? (
                    <Tooltip
                      title={`Denegación de hace ${line.denial.age_days}d`}
                    >
                      <Chip
                        size="small"
                        color="error"
                        label={`${line.denial.qty_denied} (${line.denial.age_days}d)`}
                        sx={{ height: 20, fontSize: "0.65rem" }}
                      />
                    </Tooltip>
                  ) : (
                    <Typography
                      component="span"
                      variant="caption"
                      color="text.disabled"
                    >
                      —
                    </Typography>
                  )}
                </TableCell>
                <TableCell align="center">
                  {line.abc ? (
                    <Chip
                      label={line.abc}
                      size="small"
                      sx={{
                        height: 20,
                        fontSize: "0.65rem",
                        fontWeight: 700,
                        bgcolor:
                          line.abc === "A"
                            ? "success.light"
                            : line.abc === "B"
                              ? "info.light"
                              : "grey.200",
                        color:
                          line.abc === "A"
                            ? "success.dark"
                            : line.abc === "B"
                              ? "info.dark"
                              : "text.secondary",
                      }}
                    />
                  ) : (
                    <Typography
                      component="span"
                      variant="caption"
                      color="text.disabled"
                    >
                      —
                    </Typography>
                  )}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
